import { ZEITFENSTER_KURZ, type Fristmarke, type Zeitfenster } from '@netcon-work/shared'
import { cn } from '../../lib/cn'

/**
 * **Wie eine Frist an einer Listenzeile steht** (31.08.2026,
 * [E-281](../../../../../docs/ENTSCHEIDUNGEN.md#e-281)).
 *
 * Nils, mit einem Bild von „Mein Tag": *„Wenn etwas Überfällig ist, dann müssen wir das denke
 * ich noch besser kennzeichnen (… RTM ist eigentlich 28.8. fällig gewesen, heute haben wir den
 * 31.)."*
 *
 * ## Warum dort „ohne Frist" stand
 *
 * Weil die Zeile nur `zeitfenster` las. Die Aufgabe hatte keins – ihr Termin stand in `due_at`,
 * von einem Menschen ausdrücklich gesetzt. Aus „kein Zeitfenster" wurde „ohne Frist", und damit
 * war ausgerechnet die verbindlichste Terminart die einzige ohne Wirkung.
 *
 * Jetzt kommt der Text aus `fristMarke(wirksamerTermin(…))` – dieselbe Ableitung wie in der
 * Zeitachse, im Trichter und in der Vorwarn-Mail.
 *
 * ## Warum das Zeitfenster trotzdem noch dasteht
 *
 * Es sagt etwas anderes als das Datum: **wie verbindlich** eingestuft wurde („10 T" ist eine
 * Einordnung, „bis 30.09." eine Rechnung daraus). Beides nebeneinander ist eine Zeile wie
 * vorher, nur mit der Wahrheit darin – weggelassen würde man beim nächsten Blick die Einstufung
 * vermissen.
 *
 * **Bei Überfälligem steht es nicht daneben.** Dort zählt eine Aussage, und die heißt „3 Tage
 * überfällig"; ein „10 T" daneben wäre ein Widerspruch aus der Zeit davor.
 */
export function Fristwort({
  frist,
  zeitfenster,
  herkunft = null,
  nurFrist = false,
}: {
  frist: Fristmarke | null
  zeitfenster: string | null
  /**
   * **Woher der Termin stammt** ([E-308](../../../../../docs/ENTSCHEIDUNGEN.md#e-308)) – als
   * Tooltip, nicht als Text.
   *
   * An einer Listenzeile ist „Meilenstein «Testlauf für Entwickler»" dreimal so lang wie die
   * Auskunft selbst. Sie steht trotzdem dran, weil ein geerbtes Datum sonst wie ein eigenes
   * aussieht – nur eben dort, wo man nachsieht, wenn man sie braucht.
   */
  herkunft?: string | null
  /**
   * **Nur die Frist, ohne die Einstufung daneben.**
   *
   * Für die oberen Zeilen des Vorlaufs (Nils, 02.09.2026: *„Im Vorlauf soll bei allen Sachen das
   * errechnete Fällig stehen"*). Dort zählt das Datum; „· 10 T" daneben wäre die zweite
   * Auskunft in einer Zeile, die absichtlich schmal ist.
   */
  nurFrist?: boolean
}) {
  /** Kein Termin **und** kein Fenster: dann stimmt „ohne Frist" – und nur dann. */
  if (frist === null) {
    return <span>{zeitfenster ? ZEITFENSTER_KURZ[zeitfenster as Zeitfenster] : 'ohne Frist'}</span>
  }

  const dringend = frist.stufe === 'ueberfaellig'
  const stufe = nurFrist ? null : zeigen(zeitfenster)

  return (
    <span title={herkunft ? `Termin ${herkunft}` : undefined}>
      <span
        className={cn(
          dringend && 'font-bold text-pool-sofort',
          frist.stufe === 'heute' && 'font-semibold text-warn',
        )}
      >
        {frist.text}
      </span>
      {!dringend && stufe ? ` · ${stufe}` : ''}
    </span>
  )
}

/** Die Einstufung als Kurzwort – `null`, wenn keine gesetzt ist. */
function zeigen(zeitfenster: string | null): string | null {
  if (!zeitfenster) return null
  return ZEITFENSTER_KURZ[zeitfenster as Zeitfenster] ?? zeitfenster
}
